import { isDevelopmentMode, translate } from "@/js/app/support/utils";
import { notifyErrorAlert } from "@/js/app/support/alert-and-banner";
import { isClientError, isServerError, isSessionExpired } from "@/js/app/support/http-status";
import { NOTIFICATION_INFINITE_DURATION } from "@/js/interfaces/Components/alpinejs/NotificationAlert";
import is from "@/js/app/support/is";
import { TLivewireError } from "../../exceptions";

/**
 * Try to extract the PHP exception message from a Livewire error response body.
 * - If the body is JSON (Laravel API mode), reads the `message` field.
 * - If the body is HTML (Laravel error page), extracts visible text and strips the leading status code.
 * Falls back to the HTTP status text if nothing meaningful can be extracted.
 */
function extractLivewireExceptionMessage(body: string, fallback: string): string {
    // Try JSON first (Laravel returns JSON when Accept: application/json)
    try {
        const json = JSON.parse(body) as Record<string, unknown>;
        // If it's valid JSON with a `message` field, use it
        if (is.string(json.message) && json.message.length > 0) {
            const doc = new DOMParser().parseFromString(json.message, 'text/html');
            const text = doc.body.textContent?.trim() ?? '';
            return text.length > 0 ? text : fallback;
        }
        // Valid JSON but no `message` field (e.g. #[Json()] response) — do not parse as HTML
        return fallback;
    } catch {
        // Not JSON — try parsing as an HTML error page
    }

    // Try HTML (Laravel default error pages, e.g. 403/404/500)
    try {
        const doc = new DOMParser().parseFromString(body, 'text/html');
        const text = doc.body.textContent?.replace(/\s+/g, ' ').trim() ?? '';
        // Remove the leading HTTP status code (e.g. "403 You are not allowed…" → "You are not allowed…")
        const withoutCode = text.replace(/^\d{3}\s*/, '').trim();
        return withoutCode.length > 0 ? withoutCode : fallback;
    } catch {
        // Unparseable body
    }


    return fallback;
}

/**
 * Determine the appropriate user-facing message based on the HTTP status code and response body.
 * - For session expiration, prompt the user to refresh.
 * - For client errors (4xx), attempt to extract a meaningful message from the response body.
 * - For server errors (5xx), return a generic message without exposing details.
 */
function getMessageFromStatusCode(response: Response, body: string): string {
    let message: string = '';

    if (isSessionExpired(response.status)) {
        message = translate('Session expired. Please refresh the page');
    } else if (isClientError(response.status)) {
        // 4xx: the message is relevant to the user (validation, auth, not found…)
        // Laravel sanitizes the message in production, so it's safe to show it

        /**
         * To add translations, see the laravel error page system (e.g. resources/views/errors/xxx.blade.php)
         * Per exemple, take the 404 error page. When the $exception->getMessage() is empty, we display the default message "Not Found". But if a message is set in the exception, it will be displayed instead.
         * This allows us to have translated error messages when the exception is thrown with a message, and still have a fallback translated message when it's not the case.
         * For cases not covered, only the http status text is displayed. (e.g. "Not Found" for 404 errors)
         */
        message = extractLivewireExceptionMessage(body, response.statusText);
    } else if (isServerError(response.status)) {
        // 5xx: internal server error, do not expose details to the user
        // To add translations, see the javascript translation files (e.g. resources/js/lang/fr.json)
        message = translate(response.statusText);
    }
    return message;
}

/**
 * Central handler for Livewire HTTP errors.
 * Used by both interceptAction and interceptRequest to avoid duplication.
 * interceptAction covers standard Livewire actions.
 * interceptRequest covers #[Json] actions that bypass the action pipeline.
 */
export function handleLivewireHttpError(response: Response, body: string, preventDefault: () => void): void {
    if (isDevelopmentMode()) {
        // eslint-disable-next-line no-console
        console.log("Livewire error response:", response, body);
        return;
    }
    preventDefault();

    // If the session has expired, prompt the user to refresh immediately to avoid confusion (Livewire will automatically retry the request after refresh)
    if (isSessionExpired(response.status)) {
        if (confirm(translate('Session expired. Refresh?'))) {
            window.location.reload();
        }
    }

    // if the message is not empty, and error should be displayed to the user
    const message = getMessageFromStatusCode(response, body);
    if (message.length > 0) {
        notifyErrorAlert(message.slice(0, 1000), NOTIFICATION_INFINITE_DURATION);
    }
}

export function isLivewireErrorObject(reason: unknown): reason is TLivewireError {
    return is.object<Record<string, unknown>>(reason)
        && Object.prototype.hasOwnProperty.call(reason, "body")
        && is.string(reason.body)
        && Object.prototype.hasOwnProperty.call(reason, "status")
        && is.number(reason.status)
        && Object.prototype.hasOwnProperty.call(reason, "json")
        && (is.string(reason.json) || null === reason.json)
        && Object.prototype.hasOwnProperty.call(reason, "errors")
        && (is.object<Record<string, unknown>>(reason.errors) || null === reason.errors);
}

export function canIgnoreLivewireError(reason: TLivewireError): boolean {
    return !isServerError(reason.status);
}
